import { type ReactNode } from "react";
import { modules } from "byzantine";
import type {
  WebSectionLeft,
  WebSectionRight,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

import AccountBalances from "./AccountBalances";
import AccountTotalsCard from "./AccountTotalsCard";
import ChartCard from "./ChartCard";
import CardOffers from "./CardOffers";
import ExternalAccountTiles from "./ExternalAccountTiles";
import MarketingCardContainer from "./MarketingCardContainer";
import MarketingImage from "./MarketingImage";
import RecentTransactionsCard from "./RecentTransactionsCard";
import ServicesCard from "./ServicesCard";
import SubscriptionCard from "../subscriptions/SubscriptionCard";
import ArrayPlusWidget from "../ArrayPlus/ArrayPlusWidget";
import SavvyMoneyWidget from "../savvy_money/SavvyMoneyWidget";
import {
  MARKETING_BANNER_IMAGE_ADDRESS,
  MARKETING_CARD_IMAGE_ADDRESS,
} from "./constants";
import ExternalAccountsContainer from "../../src/modules/dashboard/ExternalAccounts/ExternalAccountsContainer/ExternalAccountsContainer";
import { DashboardSectionContext } from "./types";

const { SECTION } = modules.userDisplayPreferences;

export const renderLeftSection = (
  id: WebSectionLeft,
  ctx: DashboardSectionContext
): ReactNode[] => {
  switch (id) {
    case SECTION.MARKETING_BANNER_IMAGE:
      return [
        <MarketingImage
          key="marketing_banner_image"
          address={MARKETING_BANNER_IMAGE_ADDRESS}
        />,
      ];
    case SECTION.ACCOUNTS:
      return [<AccountBalances key="accounts" />];
    case SECTION.EXTERNAL_ACCOUNTS:
      return [
        <ExternalAccountsContainer
          key="external_accounts"
          externalAccounts={ctx.externalAccounts}
          uatMicrodepAmntOne={ctx.uatMicrodepAmntOne}
          uatMicrodepAmntTwo={ctx.uatMicrodepAmntTwo}
        />,
      ];
    case SECTION.RECENT_TRANSACTIONS:
      return [
        <RecentTransactionsCard
          key="recent_transactions"
          replyTime={ctx.replyTime}
        />,
      ];
    case SECTION.CARD_OFFERS:
      return [<CardOffers key="card_offers" />];
    default:
      return [];
  }
};

export const renderRightSection = (
  id: WebSectionRight,
  ctx: DashboardSectionContext
): ReactNode[] => {
  switch (id) {
    case SECTION.ACCOUNT_TOTALS:
      return [<AccountTotalsCard key="account_totals" />];
    case SECTION.CHART_CARD:
      return [<ChartCard key="chart_card" />];
    case SECTION.CREDIT_MONITORING: {
      const nodes: ReactNode[] = [];
      if (ctx.features.array_plus_credit_reporting) {
        nodes.push(<ArrayPlusWidget key="array_plus" />);
      }
      if (ctx.features.olb_enable_credit_monitor) {
        nodes.push(
          <SavvyMoneyWidget
            key="savvy_money"
            hasErrored={ctx.hasSavvyWidgetErrored}
            refetch={ctx.fetchSavvyMoneyRedirectUrl}
            isLoading={ctx.isSavvyFetchLoading}
          />
        );
      }
      return nodes;
    }
    case SECTION.VERIFY_EXTERNAL_ACCOUNTS:
      return [
        <ExternalAccountTiles
          key="verify_external_accounts"
          externalAccounts={ctx.externalAccounts}
          uatMicrodepAmntOne={ctx.uatMicrodepAmntOne}
          uatMicrodepAmntTwo={ctx.uatMicrodepAmntTwo}
        />,
      ];
    case SECTION.MARKETING_CARD:
      return [<MarketingCardContainer key="marketing_card" />];
    case SECTION.SERVICES:
      return ctx.servicesApps
        ? [<ServicesCard key="services" services={ctx.servicesApps} />]
        : [];
    case SECTION.SUBSCRIPTIONS:
      return ctx.isSubscriptionEnabled
        ? [<SubscriptionCard key="subscriptions" />]
        : [];
    case SECTION.MARKETING_CARD_IMAGE:
      return [
        <MarketingImage
          key="marketing_card_image"
          address={MARKETING_CARD_IMAGE_ADDRESS}
        />,
      ];
    default:
      return [];
  }
};
